import React from 'react';

import { Link } from 'react-router-dom';

import { loadApplicantComments } from '../../comments/actions/load-comments';
import { setCommentsDirty } from '../../comments/actions/set-comments-dirty';
import { refreshMessages } from '../../contacts/actions/refresh-messages';
import { loadOrgEmployeeSummary } from '../../employees/actions/load-org-employee-summary';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { setOrgEmployeeSummaryDirty } from '../../employees/actions/set-org-employee-summary-dirty';
import { setOrgEmployeesDirty } from '../../employees/actions/set-org-employees-dirty';
import { fullApplicationSelector } from '../../reducers/application-selectors';
import { createGenericDispatcher } from '../action-dispatcher-factory';

import * as Api from '../../api/api';
import { GLOBAL_DIALOGS } from '../../dialogs/global-dialogs-config';
import { ScheduledMessageNotification } from '../../real-time/notifications/scheduled-message-notification';
import { jobInProgressRoute } from '../../web-routes';
import { openOrCloseGlobalDialog } from '../dialog/global-dialog';
import { addNotificationAction } from '../notifications/notifications';

import { loadApplication } from './load-job-application';
import { setApplicationDirty } from './set-application-dirty';

import {
  EMPLOYED_STATUS,
  ACCEPTED_STATUS,
  SAVED_STATUS,
  REJECTED_STATUS,
  VALIDATING_STATUS,
} from '../../config/application-status-config';

// TODO PROD-3117: Before releasing the ability to create a comment on status change, we need to
// update the klaviyo status change notification email to add that field, and test that
// it sends as expected when the comment contains HTML
export const updateApplicationStatus = (
  jobApplicantId: number,
  jobId: number,
  orgId: number,
  fromStatus: string,
  statusChange: StatusChange,
  hasEmployeePermission: boolean,
) =>
  createGenericDispatcher({
    name: 'APPLICATION_STATUS',
    loadFunction: () =>
      Api.post(`/application/${jobApplicantId}/status`, {
        body: JSON.stringify({
          statusChange,
          fromStatus,
        }),
      }),
    args: { jobApplicantId, jobId, orgId, fromStatus, statusChange },
    onSuccess: (
      dispatch: Function,
      getState: Function,
      data: { automatedTexts: Array<ScheduledMessage> },
    ) => {
      /*
    DP - 3/4/2019
    I am making a fairly big assumption here. This assumed that IF we have
    messages that need refreshing, then we have also loaded the whole
    application and it is available in the redux store. If that is not
    true, then we won't find an applicant here and take any action dependent
    on having the applicant
    */
      const applicant = fullApplicationSelector(getState(), jobApplicantId);
      const { status } = statusChange;
      if (applicant) {
        // Send Automated Messages
        if (data.automatedTexts.length > 0) {
          dispatch(
            addNotificationAction({
              key: `scheduled-message-editor-${jobApplicantId}`,
              content: (onClose: () => void) => (
                <ScheduledMessageNotification
                  onClose={onClose}
                  scheduledTextMessages={data.automatedTexts}
                  jobApplicantId={jobApplicantId}
                />
              ),
              autoHideDuration: 8000,
            }),
          );
        } else if (status === ACCEPTED_STATUS || status === SAVED_STATUS) {
          dispatch(
            addNotificationAction({
              key: `job-applicant-${jobApplicantId}`,
              message: `${status === ACCEPTED_STATUS ? 'Interested in' : 'Saved'} ${
                applicant.firstName
              } ${applicant.lastName}`,
              action: (
                <div
                  role="presentation"
                  onClick={() =>
                    dispatch(
                      openOrCloseGlobalDialog('applicantDialog', true, {
                        jobApplicantId,
                      }),
                    )
                  }
                  className="typo-link"
                >
                  {status === ACCEPTED_STATUS ? 'Reach Out Now' : 'Message Applicant'}
                </div>
              ),
            }),
          );
        }
        dispatch(refreshMessages(applicant.contactId));
        if (statusChange.comment) {
          dispatch(setCommentsDirty({ jobApplicantId: applicant.job_applicant_id }));
          dispatch(loadApplicantComments({ jobApplicantId: applicant.job_applicant_id }));
        }

        // Set Applicants or Employees Dirty
        if (status === EMPLOYED_STATUS) {
          dispatch(setOrgEmployeeSummaryDirty(applicant.orgId));
          dispatch(loadOrgEmployeeSummary(applicant.orgId));
          if (hasEmployeePermission) {
            dispatch(setOrgEmployeesDirty(applicant.orgId, EMPLOYEE_STATUS.CURRENT));
            dispatch(loadOrgEmployeesByStatus(applicant.orgId, EMPLOYEE_STATUS.CURRENT));
          }
          dispatch(
            openOrCloseGlobalDialog(GLOBAL_DIALOGS.EMPLOYED_DIALOG.key, true, {
              application: applicant,
              newStatus: status,
            }),
          );
        }

        if (status === SAVED_STATUS || status === REJECTED_STATUS || status === EMPLOYED_STATUS) {
          // reload application to get saved reason or rejected reason or new employeeId
          dispatch(setApplicationDirty(applicant.job_applicant_id));
          dispatch(loadApplication(applicant.job_applicant_id));
        }
      }

      if (fromStatus === EMPLOYED_STATUS && status === VALIDATING_STATUS) {
        dispatch(
          addNotificationAction({
            key: `job-applicant-${jobApplicantId}`,
            message: 'Successfully moved back to hiring',
            action: (
              <Link
                to={jobInProgressRoute(orgId, jobId, { jobApplicantId, status: VALIDATING_STATUS })}
                className="typo-link"
              >
                View Applicant
              </Link>
            ),
          }),
        );
      }
    },
  });
